<script>
  import { fade } from "svelte/transition";
  import { Preloader } from "./preloader";
  import { onDestroy } from "svelte";

  export let tour;
  export let logo = "./images/logo.png";
  export let loading = true;
  export let delay = 2000;
  export let callback;
  export let width = 300;
  export let maxDuration = 5 * 1000; // end loading after 5 seconds
  let active = true;

  const preloader = new Preloader(tour);

  function timeout(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  async function handleLoading() {
    const preloadPromise = preloader.initialPreload();
    const delayPromise = timeout(delay);
    const maxDurationPromise = timeout(maxDuration);

    await Promise.race([
      (async () => {
        await Promise.all([preloadPromise, delayPromise]);
        active = false;
        callback?.();
      })(),
      maxDurationPromise.then(() => {
        console.warn("Loading stopped due to maxDuration timeout");
        active = false;
        callback?.();
      }),
    ]);
  }

  $: if (!loading) handleLoading();

  onDestroy(() => {
    // Stop any running processes in Preloader
    if (preloader) preloader.stop();
    active = false;
  });
</script>

{#if active}
  <div id="loader" transition:fade={{ duration: 1000 }}>
    <div class="loader-container">
      <div class="loader-heading"><h2>Loading...</h2></div>
      <div class="loader-logo">
        <img src={logo} {width} alt="" />
      </div>
      <div class="blobs">
        <div class="blob-center"></div>
        <div class="blob"></div>
        <div class="blob"></div>
        <div class="blob"></div>
        <div class="blob"></div>
        <div class="blob"></div>
        <div class="blob"></div>
      </div>

      <svg xmlns="http://www.w3.org/2000/svg" version="1.1">
        <defs>
          <filter id="goo">
            <feGaussianBlur
              in="SourceGraphic"
              stdDeviation="10"
              result="blur"
            />
            <feColorMatrix
              in="blur"
              mode="matrix"
              values="1 0 0 0 0  0 1 0 0 0  0 0 1 0 0  0 0 0 18 -7"
              result="goo"
            />
            <feBlend in="SourceGraphic" in2="goo" />
          </filter>
        </defs>
      </svg>
    </div>
  </div>
{/if}

<style>
  :root {
    --loader-color-rgb: var(--theme-color-rgb);
    --loader-bg: var(--loader-bg, #000000);
  }

  svg {
    display: none;
  }

  #loader {
    font-family: var(--font-body), sans-serif;
    position: fixed;
    left: 0;
    top: 0;
    width: 100%;
    height: 100%;
    background: var(--loader-bg, #000000);
    z-index: 9999;
  }

  .loader-container {
    position: absolute;
    left: 50%;
    top: 50%;
    transform: translate(-50%, -50%);
  }

  .loader-logo img {
    max-width: 50vw;
  }

  @media (max-width: 768px) {
    .loader-logo img {
      max-width: 80vw;
    }
  }

  .loader-heading {
    text-align: center;
    text-transform: uppercase;
    letter-spacing: 2px;
    font-size: 10px;
    color: var(--loader-text-color, #fff);
    animation: blink-animation 1.4s steps(5, start) infinite;
    -webkit-animation: blink-animation 1.4s steps(5, start) infinite;
  }

  @keyframes blink-animation {
    to {
      visibility: hidden;
    }
  }
  @-webkit-keyframes blink-animation {
    to {
      visibility: hidden;
    }
  }

  .blobs {
    filter: url(#goo);
    width: 100%;
    height: 50px;
    position: relative;
    overflow: hidden;
    border-radius: 70px;
    transform-style: preserve-3d;
    top: 0;
  }

  .blobs .blob-center {
    transform-style: preserve-3d;
    position: absolute;
    background: rgba(0, 118, 196, 0.25);
    background: rgba(var(--loader-color-rgb), 0.25);
    top: 50%;
    left: 50%;
    width: 20px;
    height: 20px;
    transform-origin: left top;
    transform: scale(0.9) translate(-50%, -50%);
    -webkit-animation: blob-grow linear 3.4s infinite;
    animation: blob-grow linear 3.4s infinite;
    border-radius: 50%;
    box-shadow: 0 -10px 40px -5px rgba(0, 118, 196, 0.25);
    box-shadow: 0 -10px 40px -5px rgba(var(--loader-color-rgb), 0.25);
  }

  .blob {
    position: absolute;
    background: rgba(0, 118, 196, 0.25);
    background: rgba(var(--loader-color-rgb), 0.25);
    top: 50%;
    left: 50%;
    width: 20px;
    height: 20px;
    border-radius: 50%;
    -webkit-animation: blobs ease-out 3.4s infinite;
    animation: blobs ease-out 3.4s infinite;
    transform: scale(0.9) translate(-50%, -50%);
    transform-origin: center top;
    opacity: 0;
  }

  .blob:nth-child(1) {
    -webkit-animation-delay: 0.2s;
    animation-delay: 0.2s;
  }
  .blob:nth-child(2) {
    -webkit-animation-delay: 0.4s;
    animation-delay: 0.4s;
  }
  .blob:nth-child(3) {
    -webkit-animation-delay: 0.6s;
    animation-delay: 0.6s;
  }
  .blob:nth-child(4) {
    -webkit-animation-delay: 0.8s;
    animation-delay: 0.8s;
  }
  .blob:nth-child(5) {
    -webkit-animation-delay: 1s;
    animation-delay: 1s;
  }

  @-webkit-keyframes blobs {
    0% {
      opacity: 0;
      transform: scale(0) translate(calc(-330px - 50%), -50%);
    }
    1% {
      opacity: 1;
    }
    35%,
    65% {
      opacity: 1;
      transform: scale(0.9) translate(-50%, -50%);
    }
    99% {
      opacity: 1;
    }
    100% {
      opacity: 0;
      transform: scale(0) translate(calc(330px - 50%), -50%);
    }
  }

  @keyframes blobs {
    0% {
      opacity: 0;
      transform: scale(0) translate(calc(-330px - 50%), -50%);
    }
    1% {
      opacity: 1;
    }
    35%,
    65% {
      opacity: 1;
      transform: scale(0.9) translate(-50%, -50%);
    }
    99% {
      opacity: 1;
    }
    100% {
      opacity: 0;
      transform: scale(0) translate(calc(330px - 50%), -50%);
    }
  }
  @-webkit-keyframes blob-grow {
    0%,
    39% {
      transform: scale(0) translate(-50%, -50%);
    }
    40%,
    42% {
      transform: scale(1, 0.9) translate(-50%, -50%);
    }
    43%,
    44% {
      transform: scale(1.2, 1.1) translate(-50%, -50%);
    }
    45%,
    46% {
      transform: scale(1.3, 1.2) translate(-50%, -50%);
    }
    47%,
    48% {
      transform: scale(1.4, 1.3) translate(-50%, -50%);
    }
    52% {
      transform: scale(1.5, 1.4) translate(-50%, -50%);
    }
    54% {
      transform: scale(1.7, 1.6) translate(-50%, -50%);
    }
    58% {
      transform: scale(1.8, 1.7) translate(-50%, -50%);
    }
    68%,
    70% {
      transform: scale(1.7, 1.5) translate(-50%, -50%);
    }
    78% {
      transform: scale(1.6, 1.4) translate(-50%, -50%);
    }
    80%,
    81% {
      transform: scale(1.5, 1.4) translate(-50%, -50%);
    }
    82%,
    83% {
      transform: scale(1.4, 1.3) translate(-50%, -50%);
    }
    84%,
    85% {
      transform: scale(1.3, 1.2) translate(-50%, -50%);
    }
    86%,
    87% {
      transform: scale(1.2, 1.1) translate(-50%, -50%);
    }
    90%,
    91% {
      transform: scale(1, 0.9) translate(-50%, -50%);
    }
    92%,
    100% {
      transform: scale(0) translate(-50%, -50%);
    }
  }
  @keyframes blob-grow {
    0%,
    39% {
      transform: scale(0) translate(-50%, -50%);
    }
    40%,
    42% {
      transform: scale(1, 0.9) translate(-50%, -50%);
    }
    43%,
    44% {
      transform: scale(1.2, 1.1) translate(-50%, -50%);
    }
    45%,
    46% {
      transform: scale(1.3, 1.2) translate(-50%, -50%);
    }
    47%,
    48% {
      transform: scale(1.4, 1.3) translate(-50%, -50%);
    }
    52% {
      transform: scale(1.5, 1.4) translate(-50%, -50%);
    }
    54% {
      transform: scale(1.7, 1.6) translate(-50%, -50%);
    }
    58% {
      transform: scale(1.8, 1.7) translate(-50%, -50%);
    }
    68%,
    70% {
      transform: scale(1.7, 1.5) translate(-50%, -50%);
    }
    78% {
      transform: scale(1.6, 1.4) translate(-50%, -50%);
    }
    80%,
    81% {
      transform: scale(1.5, 1.4) translate(-50%, -50%);
    }
    82%,
    83% {
      transform: scale(1.4, 1.3) translate(-50%, -50%);
    }
    84%,
    85% {
      transform: scale(1.3, 1.2) translate(-50%, -50%);
    }
    86%,
    87% {
      transform: scale(1.2, 1.1) translate(-50%, -50%);
    }
    90%,
    91% {
      transform: scale(1, 0.9) translate(-50%, -50%);
    }
    92%,
    100% {
      transform: scale(0) translate(-50%, -50%);
    }
  }
</style>
